import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger,
         SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle,
         DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  FolderPlus, Upload, ChevronRight, Home as HomeIcon,
  Sparkles, Search, Archive, X,
} from "lucide-react";
import { toast } from "sonner";
import { VIS } from "./documents/constants";
import { FolderCard } from "./documents/FolderCard";
import { FilesTable } from "./documents/FilesTable";
import { ZipUploadDialog } from "./documents/ZipUploadDialog";
import { AclDialog } from "./documents/AclDialog";

/**
 * Admin document library page. State + API calls live here; every heavy
 * UI block (folder card, files table, ZIP dialog, ACL modal) lives in
 * `./documents/` so this file stays scan-friendly.
 */
export default function DocumentsPage() {
  const { properties, propertyId } = useProperty();
  const [scope, setScope] = useState(propertyId || properties[0]?.id || "");
  const [folders, setFolders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderDialog, setFolderDialog] = useState(false);
  const [folderForm, setFolderForm] = useState({ name: "", visibility: "all", description: "" });
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({ visibility: "all", description: "", display_name: "" });
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [qActive, setQActive] = useState("");
  const fileRef = useRef(null);

  // ZIP upload (R147d)
  const [zipDialog, setZipDialog] = useState(false);
  const [zipForm, setZipForm] = useState({ visibility: "all" });
  const [zipResult, setZipResult] = useState(null);

  // ACL modal (R147a)
  const [aclFolder, setAclFolder] = useState(null);
  const [aclUsers, setAclUsers] = useState([]);
  const [aclInput, setAclInput] = useState("");
  const [aclBusy, setAclBusy] = useState(false);

  // 350ms debounce so we don't hammer the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQActive(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    if (!scope) return;
    try {
      const params = { property_id: scope };
      if (qActive) params.q = qActive;
      else if (currentFolder?.id) params.folder_id = currentFolder.id;
      const [{ data: f }, { data: d }] = await Promise.all([
        api.get("/admin/documents/folders", { params: { property_id: scope } }),
        api.get("/admin/documents", { params }),
      ]);
      setFolders(f.items || []);
      setDocs(d.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
  }, [scope, currentFolder, qActive]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (properties.length && !scope) setScope(properties[0].id);
  }, [properties, scope]);

  const childFolders = useMemo(
    () => folders.filter((f) => (f.parent_id || null) === (currentFolder?.id || null)),
    [folders, currentFolder],
  );

  const searchFolders = useMemo(() => {
    if (!qActive) return [];
    const needle = qActive.toLowerCase();
    return folders.filter((f) =>
      (f.name || "").toLowerCase().includes(needle) ||
      (f.description || "").toLowerCase().includes(needle));
  }, [folders, qActive]);

  const folderById = useMemo(
    () => Object.fromEntries(folders.map((f) => [f.id, f])),
    [folders],
  );

  const breadcrumbs = useMemo(() => {
    if (!currentFolder) return [];
    const chain = [];
    let f = currentFolder;
    while (f) {
      chain.unshift(f);
      f = f.parent_id ? folderById[f.parent_id] : null;
    }
    return chain;
  }, [currentFolder, folderById]);

  const foldersToShow = qActive ? searchFolders : childFolders;

  const createFolder = async () => {
    setBusy(true);
    try {
      const body = {
        name: folderForm.name,
        visibility: folderForm.visibility,
        property_id: scope,
        parent_id: currentFolder?.id || null,
      };
      if (folderForm.description?.trim()) body.description = folderForm.description.trim();
      await api.post("/admin/documents/folders", body);
      toast.success("Folder created");
      setFolderDialog(false);
      setFolderForm({ name: "", visibility: "all", description: "" });
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Create failed"); }
    finally { setBusy(false); }
  };

  const deleteFolder = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}"?`)) return;
    try {
      await api.delete(`/admin/documents/folders/${f.id}`);
      toast.success("Folder deleted");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Delete failed"); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("property_id", scope);
      fd.append("visibility", uploadForm.visibility);
      if (currentFolder?.id) fd.append("folder_id", currentFolder.id);
      if (uploadForm.display_name) fd.append("display_name", uploadForm.display_name);
      if (uploadForm.description) fd.append("description", uploadForm.description);
      await api.post("/admin/documents/upload", fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      setUploadDialog(false);
      setUploadForm({ visibility: "all", description: "", display_name: "" });
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const uploadZip = async (file, ref) => {
    if (!file) return;
    setBusy(true);
    setZipResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("property_id", scope);
      fd.append("visibility", zipForm.visibility);
      if (currentFolder?.id) fd.append("folder_id", currentFolder.id);
      const { data } = await api.post("/admin/documents/upload-zip", fd,
                                       { headers: { "Content-Type": "multipart/form-data" } });
      setZipResult(data);
      const up = data.counts?.uploaded || 0;
      toast.success(`Extracted ${up} file${up === 1 ? "" : "s"} from ZIP`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "ZIP upload failed"); }
    finally { setBusy(false); if (ref?.current) ref.current.value = ""; }
  };

  const download = async (d) => {
    const url = `${API}/admin/documents/${d.id}/download`;
    try {
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error("download failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = d.filename || d.name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
    } catch { toast.error("Download failed"); }
  };

  const deleteDoc = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try {
      await api.delete(`/admin/documents/${d.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Delete failed"); }
  };

  const openAcl = async (folder) => {
    setAclFolder(folder);
    setAclUsers([]);
    setAclInput("");
    setAclBusy(true);
    try {
      const { data } = await api.get(`/admin/documents/folders/${folder.id}/acl`);
      setAclUsers(data.users || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load ACL"); }
    finally { setAclBusy(false); }
  };

  const aclAdd = async () => {
    const val = aclInput.trim();
    if (!val) return;
    setAclBusy(true);
    try {
      await api.post(`/admin/documents/folders/${aclFolder.id}/acl`,
                     { add: [val], remove: [] });
      const { data } = await api.get(`/admin/documents/folders/${aclFolder.id}/acl`);
      setAclUsers(data.users || []);
      setAclInput("");
      await load();
      toast.success("Access granted");
    } catch (e) { toast.error(apiErrorMessage(e) || "Grant failed"); }
    finally { setAclBusy(false); }
  };

  const aclRemove = async (uid) => {
    setAclBusy(true);
    try {
      await api.post(`/admin/documents/folders/${aclFolder.id}/acl`,
                     { add: [], remove: [uid] });
      const { data } = await api.get(`/admin/documents/folders/${aclFolder.id}/acl`);
      setAclUsers(data.users || []);
      await load();
      toast.success("Access removed");
    } catch (e) { toast.error(apiErrorMessage(e) || "Revoke failed"); }
    finally { setAclBusy(false); }
  };

  const seedHoaFolders = async () => {
    if (!scope) return;
    if (!window.confirm("Seed the standard HOA folder set (Governing Documents, Meeting Minutes, Committees, Proposals & Bids, Financials, Forms & Applications, Insurance, Admin Only)? Existing folders with matching names will be skipped.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/documents/seed-hoa-folders", { property_id: scope });
      const created = (data.created || []).length;
      const skipped = (data.skipped || []).length;
      if (created === 0 && skipped > 0) toast.info(`All ${skipped} standard folders already exist.`);
      else toast.success(`Created ${created} folder${created === 1 ? "" : "s"}${skipped ? ` · skipped ${skipped}` : ""}`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Seed failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="Library" title="Documents"
                   subtitle="Governing docs, budgets, minutes, and any file your community needs to keep on hand." />

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Select value={scope} onValueChange={(v) => { setScope(v); setCurrentFolder(null); }}>
          <SelectTrigger className="w-56 h-9 bg-white/[0.04] border-white/10"
                          data-testid="docs-scope"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search across every folder…"
                 className="pl-8 h-9 w-72 bg-white/[0.04] border-white/10"
                 data-testid="docs-search" />
          {q && (
            <button onClick={() => setQ("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                    data-testid="docs-search-clear">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <Button onClick={() => setFolderDialog(true)} variant="outline"
                className="border-white/10 h-9 rounded-full" data-testid="docs-new-folder">
          <FolderPlus className="h-4 w-4 mr-1" /> New folder
        </Button>
        <Button onClick={() => setUploadDialog(true)}
                className="bg-orange-500 hover:bg-orange-600 rounded-full h-9"
                data-testid="docs-upload-btn">
          <Upload className="h-4 w-4 mr-1" /> Upload file
        </Button>
        <Button onClick={() => { setZipDialog(true); setZipResult(null); }} variant="outline"
                className="border-white/10 h-9 rounded-full" data-testid="docs-upload-zip-btn">
          <Archive className="h-4 w-4 mr-1" /> Upload ZIP
        </Button>
        <Button onClick={seedHoaFolders} variant="outline"
                className="border-orange-500/40 text-orange-300 hover:bg-orange-500/[0.10] h-9 rounded-full"
                data-testid="docs-seed-hoa-btn">
          <Sparkles className="h-4 w-4 mr-1" /> Seed HOA folders
        </Button>
        <div className="ml-auto text-xs text-zinc-500">
          {docs.length} file{docs.length === 1 ? "" : "s"} · {foldersToShow.length} folder{foldersToShow.length === 1 ? "" : "s"}
        </div>
      </div>

      {qActive && (
        <div className="mb-3 rounded-lg border border-orange-500/30 bg-orange-500/[0.06] px-3 py-2 flex items-center gap-2 text-xs text-orange-300"
             data-testid="docs-search-banner">
          <Search className="h-3.5 w-3.5" />
          Showing results for <span className="font-semibold">&ldquo;{qActive}&rdquo;</span> across every folder in this community.
          <button onClick={() => setQ("")} className="ml-auto text-orange-200/70 hover:text-orange-100">
            Clear search
          </button>
        </div>
      )}

      {!qActive && (
        <div className="flex items-center gap-1 text-xs text-zinc-400 mb-3"
             data-testid="docs-breadcrumbs">
          <button onClick={() => setCurrentFolder(null)}
                  className="hover:text-white inline-flex items-center gap-1"
                  data-testid="docs-crumb-root">
            <HomeIcon className="h-3 w-3" /> Root
          </button>
          {breadcrumbs.map((f) => (
            <span key={f.id} className="inline-flex items-center gap-1">
              <ChevronRight className="h-3 w-3 text-zinc-600" />
              <button onClick={() => setCurrentFolder(f)} className="hover:text-white"
                      data-testid={`docs-crumb-${f.id}`}>
                {f.name}
              </button>
            </span>
          ))}
        </div>
      )}

      {foldersToShow.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-3">
          {foldersToShow.map((f) => (
            <FolderCard key={f.id} folder={f}
                         onOpen={(folder) => { setCurrentFolder(folder); if (qActive) setQ(""); }}
                         onDelete={deleteFolder}
                         onManageAcl={openAcl} />
          ))}
        </div>
      )}

      <FilesTable docs={docs}
                   folderById={folderById}
                   qActive={qActive}
                   onOpenFolder={(f) => { setCurrentFolder(f); setQ(""); }}
                   onDownload={download}
                   onDelete={deleteDoc} />

      {/* New folder dialog — kept inline (small, one-off) */}
      <Dialog open={folderDialog} onOpenChange={setFolderDialog}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[440px]"
                       data-testid="docs-folder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="h-4 w-4 text-orange-400" /> New folder
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              {currentFolder ? `Nested under ${currentFolder.name}` : "At the root of this community's library"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-zinc-400">Name</Label>
              <Input value={folderForm.name}
                     onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
                     placeholder="Governing Documents"
                     className="bg-white/[0.04] border-white/10"
                     data-testid="docs-folder-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Description (optional)</Label>
              <Textarea value={folderForm.description}
                         onChange={(e) => setFolderForm({ ...folderForm, description: e.target.value })}
                         placeholder="Short helper text residents will see on the folder card"
                         rows={2} maxLength={400}
                         className="bg-white/[0.04] border-white/10 resize-none"
                         data-testid="docs-folder-description" />
              <div className="text-[10px] text-zinc-600 text-right">
                {folderForm.description.length}/400
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Who can see this folder</Label>
              <Select value={folderForm.visibility}
                       onValueChange={(v) => setFolderForm({ ...folderForm, visibility: v })}>
                <SelectTrigger className="bg-white/[0.04] border-white/10"
                                data-testid="docs-folder-visibility"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                  {VIS.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderDialog(false)}
                    disabled={busy} className="border-white/10">Cancel</Button>
            <Button onClick={createFolder} disabled={busy || !folderForm.name}
                    className="bg-orange-500 hover:bg-orange-600" data-testid="docs-folder-save">
              {busy ? "Creating…" : "Create folder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload file dialog — kept inline */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[480px]"
                       data-testid="docs-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-4 w-4 text-orange-400" /> Upload file
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              PDF, Word, Excel, PowerPoint, TXT, CSV or image. Max 25 MB.
              {currentFolder ? ` Uploaded into "${currentFolder.name}".` : " Uploaded to the root."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-zinc-400">Display name (optional)</Label>
              <Input value={uploadForm.display_name}
                     onChange={(e) => setUploadForm({ ...uploadForm, display_name: e.target.value })}
                     placeholder="Falls back to the file name"
                     className="bg-white/[0.04] border-white/10"
                     data-testid="docs-upload-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Description (optional)</Label>
              <Input value={uploadForm.description}
                     onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                     placeholder="Short note for residents…"
                     className="bg-white/[0.04] border-white/10"
                     data-testid="docs-upload-desc" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Who can see it</Label>
              <Select value={uploadForm.visibility}
                       onValueChange={(v) => setUploadForm({ ...uploadForm, visibility: v })}>
                <SelectTrigger className="bg-white/[0.04] border-white/10"
                                data-testid="docs-upload-visibility"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                  {VIS.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <input ref={fileRef} type="file" hidden
                   onChange={(e) => upload(e.target.files?.[0])}
                   accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.webp"
                   data-testid="docs-upload-file-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)}
                    disabled={busy} className="border-white/10">Cancel</Button>
            <Button onClick={() => fileRef.current?.click()} disabled={busy}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="docs-upload-pick-file">
              {busy ? "Uploading…" : "Pick file & upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ZipUploadDialog open={zipDialog}
                        onOpenChange={(o) => { setZipDialog(o); if (!o) setZipResult(null); }}
                        currentFolder={currentFolder}
                        form={zipForm} setForm={setZipForm}
                        busy={busy}
                        result={zipResult}
                        onPickFile={uploadZip} />

      <AclDialog folder={aclFolder}
                  users={aclUsers}
                  busy={aclBusy}
                  input={aclInput} setInput={setAclInput}
                  onAdd={aclAdd}
                  onRemove={aclRemove}
                  onClose={() => setAclFolder(null)} />
    </div>
  );
}
